import '~/assets/styles/index.scss';

import React, { useEffect } from 'react';
import { GeoLocationProvider } from '~/app/context';
import { ConsentProvider } from '~/app/consent-context';
import { isAdsAllowed } from '~/shared/consent/consent';
import { removeStrayBodyText } from '~/shared/utils/remove-stray-body-text';

const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'cq_cmp',
  'cq_con',
  'cq_med',
  'cq_net',
  'cq_plt',
  'cq_src',
  'cq_term',
  'city',
];
const FIRST_TOUCH_KEY = 'analytics_first_touch_v1';
const LAST_TOUCH_KEY = 'analytics_last_touch_v1';
const ATTR_COOKIE_PREFIX = 'ctm_attr_';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

const generateEventId = () => {
  return '_lp_' + Math.random().toString(36).slice(2, 11);
};

const getCookie = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift() || '';
  return '';
};

const readAttribution = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const url = new URL(window.location.href);
  const result = {};

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value) {
      result[key] = value;
    }
  });

  result.landing_url = url.href;
  result.landing_path = `${url.pathname}${url.search}`;
  result.referrer = document.referrer || '';

  return result;
};

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
};

const setAttributionCookies = (firstTouch, lastTouch) => {
  const writeObject = (prefix, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      setCookie(`${ATTR_COOKIE_PREFIX}${prefix}_${key}`, String(value));
    });
  };

  writeObject('first', firstTouch);
  writeObject('last', lastTouch);
};

const persistAttribution = (attribution) => {
  try {
    const firstTouchRaw = window.localStorage.getItem(FIRST_TOUCH_KEY);
    if (!firstTouchRaw) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attribution));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(attribution));
  } catch (error) {
    // no-op
  }
};

const readPersistedAttribution = () => {
  try {
    const firstTouch = JSON.parse(window.localStorage.getItem(FIRST_TOUCH_KEY) || '{}');
    const lastTouch = JSON.parse(window.localStorage.getItem(LAST_TOUCH_KEY) || '{}');
    return { firstTouch, lastTouch };
  } catch (error) {
    return { firstTouch: {}, lastTouch: {} };
  }
};

const primeDataLayer = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const currentAttribution = readAttribution();
  persistAttribution(currentAttribution);
  const { firstTouch, lastTouch } = readPersistedAttribution();
  setAttributionCookies(firstTouch, lastTouch);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'traffic_context_ready',
    page_url: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || '',
    source_data: {
      first_touch: firstTouch,
      last_touch: lastTouch,
    },
    first_touch: firstTouch,
    last_touch: lastTouch,
  });

  return currentAttribution;
};

const runCtmWhenReady = () => {
  if (typeof window === 'undefined' || !isAdsAllowed()) {
    return;
  }

  const maxAttempts = 20;
  let attempts = 0;

  const tick = () => {
    attempts += 1;
    if (window.__ctm?.main?.runNow) {
      window.__ctm.main.runNow();
      return;
    }
    if (attempts < maxAttempts) {
      setTimeout(tick, 500);
    }
  };

  tick();
};

// Fallback only: gatsby-ssr.js already starts GTM from <head>.
const loadGtm = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.__gtmLoaded) {
    return;
  }

  const gtmId = process.env.GTM_CODE || 'GTM-TGGL556';
  if (!gtmId) {
    return;
  }

  window.__gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(script);
};

const sendServerEvent = async (eventName) => {
  if (typeof window === 'undefined' || !isAdsAllowed()) {
    return;
  }

  const payload = {
    event_name: eventName,
    event_id: generateEventId(),
    event_source_url: window.location.href,
    client_user_agent: window.navigator.userAgent,
    fbc: getCookie('_fbc'),
    fbp: getCookie('_fbp'),
  };

  try {
    await fetch('/api/trackEvent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // no-op
  }
};

const HEADER_OFFSET = 122;
const FOOTER_HASH_IDS = new Set([
  'footer-terms-and-conditions',
  'footer-privacy-policy',
  'footer-cookie-settings',
]);

const scrollToHashTarget = (hash) => {
  if (typeof document === 'undefined' || !hash) {
    return false;
  }

  const id = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!id) {
    return false;
  }

  let element = document.getElementById(id);
  if (!element) {
    return false;
  }

  let offset = HEADER_OFFSET;

  // On mobile the #zip section shows the map above the form, so land on the
  // form itself; the sticky mobile header is slightly taller than the desktop one.
  if (id === 'zip' && window.innerWidth <= 900) {
    const form = document.querySelector('.zip__info');
    if (form) {
      element = form;
      offset = 150;
    }
  }

  const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

  return true;
};

const ThirdPartyDeferredLoader = () => {
  useEffect(() => {
    primeDataLayer();
    loadGtm();
    // Swap again once React has hydrated: hydration can put the static
    // numbers back over a swap CTM made while the page was still parsing.
    runCtmWhenReady();

    const onPhoneClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const anchor = target.closest('a[href^="tel:"]');
      if (!anchor) {
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'phone_click',
        phone_href: anchor.getAttribute('href') || '',
      });
      sendServerEvent('Lead');
    };

    const onHashLinkClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const link = target.closest('a[href^="#"]');
      if (!link) {
        return;
      }

      const href = link.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      const id = href.slice(1);
      if (!document.getElementById(id)) {
        return;
      }

      event.preventDefault();
      scrollToHashTarget(href);

      // Skip links target a focusable region (tabindex="-1"); move focus there
      // too, or the next Tab would start from the top of the page again.
      const destination = document.getElementById(id);
      if (destination.getAttribute('tabindex') === '-1') {
        destination.focus({ preventScroll: true });
      }
    };

    document.addEventListener('click', onPhoneClick);
    document.addEventListener('click', onHashLinkClick);

    return () => {
      document.removeEventListener('click', onPhoneClick);
      document.removeEventListener('click', onHashLinkClick);
    };
  }, []);

  return null;
};

export const onInitialClientRender = () => {
  if (typeof window === 'undefined') {
    return;
  }

  removeStrayBodyText();

  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }

  const hash = window.location.hash;
  if (!hash) {
    return;
  }

  const id = decodeURIComponent(hash.slice(1));
  if (!FOOTER_HASH_IDS.has(id)) {
    return;
  }

  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}`);
  window.scrollTo(0, 0);
};

export const shouldUpdateScroll = ({ routerProps }) => {
  const hash = routerProps?.location?.hash;
  if (!hash) {
    return true;
  }

  const id = decodeURIComponent(hash.slice(1));
  if (FOOTER_HASH_IDS.has(id)) {
    window.scrollTo(0, 0);
    return false;
  }

  return true;
};

export const wrapRootElement = ({ element }) => {
  return (
    <GeoLocationProvider>
      <ConsentProvider>
        <ThirdPartyDeferredLoader />
        {element}
      </ConsentProvider>
    </GeoLocationProvider>
  );
};

export const onRouteUpdate = () => {
  sendServerEvent('PageView');
};
